import { supabase } from '@/shared/lib/supabase';

/**
 * adminFinancialsService — cash-basis financial reports.
 *
 * Revenue is sourced from `payment.amount` (status='success') grouped by
 * month-of-paid_at and bucketed by linked `invoice.invoice_kind`.
 *
 * Expenses combine:
 *   1. Manually-recorded `expense` rows (one-offs + ad-hoc costs)
 *   2. Auto recurring fixed costs from `system_setting.monthly_recurring_expenses`
 *      (whm_hosting / internet / infrastructure)
 *   3. PayFast fee = revenue × `system_setting.payfast_fee_pct`
 *
 * Projections use CURRENT MRR (not historical payment averages) — the
 * recurring book of business is what actually projects forward; lumpy
 * one-off payments would skew a rolling average.
 */

export type ExpenseCategory =
  | 'domains_topup' | 'whm_hosting' | 'infrastructure'
  | 'tooling' | 'salary' | 'marketing' | 'other'
  | 'internet' | 'payfast_fees';

export const EXPENSE_CATEGORY_LABEL: Record<ExpenseCategory, string> = {
  domains_topup: 'domains.co.za top-up',
  whm_hosting: 'WHM hosting (domains.co.za reseller)',
  internet: 'Internet',
  infrastructure: 'Infrastructure (Supabase / Resend)',
  payfast_fees: 'PayFast fees',
  tooling: 'Tooling & subscriptions',
  salary: 'Salary',
  marketing: 'Marketing',
  other: 'Other',
};

/** Categories the user can pick when manually recording an expense.
 *  `payfast_fees` is auto-computed from revenue and is never recorded manually. */
export const MANUAL_EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'domains_topup', 'whm_hosting', 'internet', 'infrastructure',
  'tooling', 'salary', 'marketing', 'other',
];

export type ExpenseRow = {
  id: string;
  amount: number;
  category: ExpenseCategory;
  description: string;
  incurred_on: string;
  notes: string | null;
  recorded_by: string | null;
  created_at: string;
};

export type RevenueBucket = {
  hosting: number;
  domains: number;
  other: number;
  total: number;
};

export type ExpenseBucket = Record<ExpenseCategory, number> & { total: number };

export type IncomeStatement = {
  period_label: string;
  period_start: string;
  period_end: string;
  revenue: RevenueBucket;
  expense: ExpenseBucket;
  net: number;
};

export type MonthlyDatum = {
  month_start: string;
  month_label: string;
  revenue: RevenueBucket;
  expense: ExpenseBucket;
  net: number;
};

export type CurrentMrr = {
  hosting_mrr: number;
  domains_mrr: number;
  total_mrr: number;
  hosting_active_links: number;
  domains_active_links: number;
};

interface PaymentRow {
  amount: number;
  paid_at: string | null;
  created_at: string;
  status: string;
  invoice_id: string | null;
}

interface InvoiceKindLite {
  id: string;
  invoice_kind: string;
}

const HOSTING_KINDS = new Set(['hosting_renewal', 'hosting_provisioning', 'plan_change']);
const DOMAIN_KINDS = new Set(['domain_registration', 'domain_renewal']);

function emptyRevenue(): RevenueBucket {
  return { hosting: 0, domains: 0, other: 0, total: 0 };
}
function emptyExpense(): ExpenseBucket {
  return {
    domains_topup: 0, whm_hosting: 0, internet: 0, infrastructure: 0,
    payfast_fees: 0, tooling: 0, salary: 0, marketing: 0, other: 0, total: 0,
  };
}
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
function recomputeExpenseTotal(b: ExpenseBucket): number {
  return round2(
    b.domains_topup + b.whm_hosting + b.internet + b.infrastructure +
    b.payfast_fees + b.tooling + b.salary + b.marketing + b.other,
  );
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function startOfMonth(year: number, month: number): Date {
  return new Date(Date.UTC(year, month, 1));
}
function endOfMonth(year: number, month: number): Date {
  return new Date(Date.UTC(year, month + 1, 0, 23, 59, 59));
}

interface RecurringExpenseConfig {
  recurring: Partial<Record<ExpenseCategory, number>>;
  payfastFeePct: number;
}
async function loadRecurringConfig(): Promise<RecurringExpenseConfig> {
  const [{ data: rec }, { data: fee }] = await Promise.all([
    supabase.from('system_setting').select('value').eq('key', 'monthly_recurring_expenses').maybeSingle(),
    supabase.from('system_setting').select('value').eq('key', 'payfast_fee_pct').maybeSingle(),
  ]);
  return {
    recurring: (rec?.value as Partial<Record<ExpenseCategory, number>>) ?? {},
    payfastFeePct: Number(fee?.value ?? 0.03),
  };
}

async function loadPayments(fromIso: string, toIso: string): Promise<{
  payments: PaymentRow[];
  invoiceKindById: Map<string, string>;
}> {
  const { data: payments, error } = await supabase
    .from('payment')
    .select('amount, paid_at, created_at, status, invoice_id')
    .eq('status', 'success')
    .gte('paid_at', fromIso)
    .lte('paid_at', toIso);
  if (error) throw new Error(`Could not load payments: ${error.message}`);

  const invoiceIds = [...new Set((payments ?? []).map((p) => p.invoice_id).filter((v): v is string => !!v))];
  const invoiceKindById = new Map<string, string>();
  if (invoiceIds.length > 0) {
    const { data: invoices, error: invErr } = await supabase
      .from('invoice')
      .select('id, invoice_kind')
      .in('id', invoiceIds);
    if (invErr) throw new Error(`Could not load invoice kinds: ${invErr.message}`);
    for (const inv of (invoices ?? []) as InvoiceKindLite[]) {
      invoiceKindById.set(inv.id, inv.invoice_kind);
    }
  }
  return { payments: (payments ?? []) as PaymentRow[], invoiceKindById };
}

async function loadExpenses(fromIso: string, toIso: string): Promise<ExpenseRow[]> {
  const { data, error } = await supabase
    .from('expense')
    .select('id, amount, category, description, incurred_on, notes, recorded_by, created_at')
    .gte('incurred_on', fromIso.slice(0, 10))
    .lte('incurred_on', toIso.slice(0, 10))
    .order('incurred_on', { ascending: false });
  if (error) throw new Error(`Could not load expenses: ${error.message}`);
  return (data ?? []) as ExpenseRow[];
}

function bucketRevenue(payments: PaymentRow[], invoiceKindById: Map<string, string>): RevenueBucket {
  const out = emptyRevenue();
  for (const p of payments) {
    const amt = Number(p.amount);
    const kind = p.invoice_id ? invoiceKindById.get(p.invoice_id) : undefined;
    if (kind && HOSTING_KINDS.has(kind)) out.hosting += amt;
    else if (kind && DOMAIN_KINDS.has(kind)) out.domains += amt;
    else out.other += amt;
  }
  out.hosting = round2(out.hosting);
  out.domains = round2(out.domains);
  out.other = round2(out.other);
  out.total = round2(out.hosting + out.domains + out.other);
  return out;
}

function bucketExpenses(expenses: ExpenseRow[]): ExpenseBucket {
  const out = emptyExpense();
  for (const e of expenses) {
    const amt = Number(e.amount);
    out[e.category] = round2(out[e.category] + amt);
  }
  out.total = recomputeExpenseTotal(out);
  return out;
}

/**
 * Augment a monthly expense bucket with:
 *   - Recurring fixed costs (whm_hosting / internet / infrastructure) IF
 *     no manual entry already exists in that category for the month
 *     (avoids double-counting if you've logged the actual invoice).
 *   - PayFast fee = revenue × payfastFeePct (always added; never overlaps
 *     a manual entry since payfast_fees isn't a manual category).
 */
function applyAutoExpenses(
  bucket: ExpenseBucket,
  revenueTotal: number,
  cfg: RecurringExpenseConfig,
  manualMonthCategories: Set<ExpenseCategory>,
): ExpenseBucket {
  const out: ExpenseBucket = { ...bucket };
  for (const [cat, amt] of Object.entries(cfg.recurring) as Array<[ExpenseCategory, number]>) {
    if (!amt || amt <= 0) continue;
    if (manualMonthCategories.has(cat)) continue;
    out[cat] = round2(out[cat] + amt);
  }
  out.payfast_fees = round2(out.payfast_fees + revenueTotal * cfg.payfastFeePct);
  out.total = recomputeExpenseTotal(out);
  return out;
}

export const adminFinancialsService = {
  async getCurrentMrr(): Promise<CurrentMrr> {
    const { data: metrics, error: metricsErr } = await supabase
      .from('v_admin_dashboard_metrics')
      .select('mrr')
      .single();
    if (metricsErr) throw new Error(`Could not load MRR: ${metricsErr.message}`);
    const hosting_mrr = Number((metrics as { mrr: number } | null)?.mrr ?? 0);

    const [{ data: links }, { data: pricingRow }, { data: hostingLinks }] = await Promise.all([
      supabase.from('domain_link').select('domain_name').is('unlinked_at', null).eq('auto_renew', true),
      supabase.from('system_setting').select('value').eq('key', 'domains_pricing').maybeSingle(),
      supabase.from('customer_link').select('id').is('unlinked_at', null).not('service_plan_id', 'is', null),
    ]);

    const pricing = (pricingRow?.value ?? {}) as Record<string, { renew?: number }>;
    let domains_mrr = 0;
    let domains_active_links = 0;
    for (const l of (links ?? []) as Array<{ domain_name: string }>) {
      domains_active_links++;
      const dot = l.domain_name.indexOf('.');
      const tld = dot === -1 ? '' : l.domain_name.slice(dot + 1);
      const annual = pricing[tld]?.renew;
      if (annual && annual > 0) domains_mrr += annual / 12;
    }
    domains_mrr = round2(domains_mrr);

    return {
      hosting_mrr: round2(hosting_mrr),
      domains_mrr,
      total_mrr: round2(hosting_mrr + domains_mrr),
      hosting_active_links: (hostingLinks ?? []).length,
      domains_active_links,
    };
  },

  /** Income statement for a single calendar month (YYYY-MM). */
  async getMonthlyStatement(yearMonth: string): Promise<IncomeStatement> {
    const parts = yearMonth.split('-');
    const year = Number(parts[0] ?? 1970);
    const month = Number(parts[1] ?? 1) - 1;
    const start = startOfMonth(year, month);
    const end = endOfMonth(year, month);
    const [{ payments, invoiceKindById }, expenses, cfg] = await Promise.all([
      loadPayments(start.toISOString(), end.toISOString()),
      loadExpenses(start.toISOString(), end.toISOString()),
      loadRecurringConfig(),
    ]);
    const revenue = bucketRevenue(payments, invoiceKindById);
    const manualBucket = bucketExpenses(expenses);
    const manualSet = new Set<ExpenseCategory>(expenses.map((e) => e.category));
    const expense = applyAutoExpenses(manualBucket, revenue.total, cfg, manualSet);
    return {
      period_label: monthLabel(start),
      period_start: start.toISOString().slice(0, 10),
      period_end: end.toISOString().slice(0, 10),
      revenue,
      expense,
      net: round2(revenue.total - expense.total),
    };
  },

  /** Trailing 12 calendar months ending at the current month, oldest first. */
  async getTwelveMonthReport(): Promise<MonthlyDatum[]> {
    const now = new Date();
    const months: { year: number; month: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
      months.push({ year: d.getUTCFullYear(), month: d.getUTCMonth() });
    }
    const first = months[0]!;
    const last = months[months.length - 1]!;
    const start = startOfMonth(first.year, first.month);
    const end = endOfMonth(last.year, last.month);
    const [{ payments, invoiceKindById }, expenses, cfg] = await Promise.all([
      loadPayments(start.toISOString(), end.toISOString()),
      loadExpenses(start.toISOString(), end.toISOString()),
      loadRecurringConfig(),
    ]);

    return months.map((m) => {
      const ms = startOfMonth(m.year, m.month);
      const me = endOfMonth(m.year, m.month);
      const inMonth = (ts: string | null) => {
        if (!ts) return false;
        const t = new Date(ts).getTime();
        return t >= ms.getTime() && t <= me.getTime();
      };
      const monthPayments = payments.filter((p) => inMonth(p.paid_at));
      const monthExpenses = expenses.filter((e) => {
        const t = new Date(e.incurred_on + 'T00:00:00Z').getTime();
        return t >= ms.getTime() && t <= me.getTime();
      });
      const rev = bucketRevenue(monthPayments, invoiceKindById);
      const manualBucket = bucketExpenses(monthExpenses);
      const manualSet = new Set<ExpenseCategory>(monthExpenses.map((e) => e.category));
      const exp = applyAutoExpenses(manualBucket, rev.total, cfg, manualSet);
      return {
        month_start: ms.toISOString().slice(0, 10),
        month_label: monthLabel(ms),
        revenue: rev,
        expense: exp,
        net: round2(rev.total - exp.total),
      };
    });
  },

  /** Calendar-year totals for the trailing N years (default 3). */
  async getYearlyReport(yearsBack = 3): Promise<IncomeStatement[]> {
    const now = new Date();
    const cfg = await loadRecurringConfig();
    const out: IncomeStatement[] = [];
    for (let i = yearsBack - 1; i >= 0; i--) {
      const year = now.getUTCFullYear() - i;
      const start = new Date(Date.UTC(year, 0, 1));
      const end = new Date(Date.UTC(year, 11, 31, 23, 59, 59));
      const [{ payments, invoiceKindById }, expenses] = await Promise.all([
        loadPayments(start.toISOString(), end.toISOString()),
        loadExpenses(start.toISOString(), end.toISOString()),
      ]);
      const revenue = bucketRevenue(payments, invoiceKindById);
      const manualBucket = bucketExpenses(expenses);
      const monthsElapsed = i === 0 ? now.getUTCMonth() + 1 : 12;
      const yearlyRecurring: ExpenseBucket = emptyExpense();
      for (const [cat, amt] of Object.entries(cfg.recurring) as Array<[ExpenseCategory, number]>) {
        if (!amt || amt <= 0) continue;
        yearlyRecurring[cat] = round2(amt * monthsElapsed);
      }
      yearlyRecurring.payfast_fees = round2(revenue.total * cfg.payfastFeePct);
      const expense: ExpenseBucket = emptyExpense();
      for (const cat of Object.keys(EXPENSE_CATEGORY_LABEL) as ExpenseCategory[]) {
        expense[cat] = round2(manualBucket[cat] + yearlyRecurring[cat]);
      }
      expense.total = recomputeExpenseTotal(expense);
      out.push({
        period_label: String(year),
        period_start: start.toISOString().slice(0, 10),
        period_end: end.toISOString().slice(0, 10),
        revenue,
        expense,
        net: round2(revenue.total - expense.total),
      });
    }
    return out;
  },

  /**
   * Forward 12-month projection based on CURRENT MRR (not trailing payment
   * average — that produces nonsense like R1656.93 from one lumpy month
   * divided by three). Each projected month assumes the recurring book of
   * business holds at today's MRR. Expenses each month = configured fixed
   * recurring costs + PayFast fee on projected revenue.
   */
  async getProjections(): Promise<{
    history: MonthlyDatum[];
    projection: MonthlyDatum[];
    forecast_year_end: { revenue: number; expense: number; net: number };
    method: string;
  }> {
    const [history, mrr, cfg] = await Promise.all([
      this.getTwelveMonthReport(),
      this.getCurrentMrr(),
      loadRecurringConfig(),
    ]);

    const baseRevTotal = mrr.total_mrr;
    const mixHosting = baseRevTotal > 0 ? mrr.hosting_mrr / baseRevTotal : 0;
    const mixDomains = baseRevTotal > 0 ? mrr.domains_mrr / baseRevTotal : 0;

    const buildExpense = (revenueTotal: number): ExpenseBucket => {
      const e = emptyExpense();
      for (const [cat, amt] of Object.entries(cfg.recurring) as Array<[ExpenseCategory, number]>) {
        if (!amt || amt <= 0) continue;
        e[cat] = round2(amt);
      }
      e.payfast_fees = round2(revenueTotal * cfg.payfastFeePct);
      e.total = recomputeExpenseTotal(e);
      return e;
    };

    const now = new Date();
    const projection: MonthlyDatum[] = [];
    for (let i = 1; i <= 12; i++) {
      const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + i, 1));
      const rev: RevenueBucket = {
        hosting: round2(baseRevTotal * mixHosting),
        domains: round2(baseRevTotal * mixDomains),
        other: 0,
        total: round2(baseRevTotal),
      };
      const exp = buildExpense(rev.total);
      projection.push({
        month_start: d.toISOString().slice(0, 10),
        month_label: monthLabel(d),
        revenue: rev,
        expense: exp,
        net: round2(rev.total - exp.total),
      });
    }

    const ytdHistory = history.filter((m) => m.month_start.startsWith(String(now.getUTCFullYear())));
    const ytdRev = ytdHistory.reduce((s, m) => s + m.revenue.total, 0);
    const ytdExp = ytdHistory.reduce((s, m) => s + m.expense.total, 0);
    const remaining = 12 - (now.getUTCMonth() + 1);
    const projectedRemainder = projection.slice(0, Math.max(0, remaining));
    const projRev = projectedRemainder.reduce((s, m) => s + m.revenue.total, 0);
    const projExp = projectedRemainder.reduce((s, m) => s + m.expense.total, 0);

    const recurringList = Object.entries(cfg.recurring)
      .filter(([, v]) => Number(v) > 0)
      .map(([k, v]) => `${EXPENSE_CATEGORY_LABEL[k as ExpenseCategory]} R${Number(v).toFixed(2)}`)
      .join(' · ');

    return {
      history,
      projection,
      forecast_year_end: {
        revenue: round2(ytdRev + projRev),
        expense: round2(ytdExp + projExp),
        net: round2((ytdRev + projRev) - (ytdExp + projExp)),
      },
      method:
        `Each month projected at current total MRR R${baseRevTotal.toFixed(2)} ` +
        `(${mrr.hosting_active_links} hosting subscription${mrr.hosting_active_links === 1 ? '' : 's'} + ` +
        `${mrr.domains_active_links} auto-renewing domain${mrr.domains_active_links === 1 ? '' : 's'}). ` +
        `Expenses each month = recurring fixed (${recurringList || 'not configured'}) + ` +
        `PayFast ${(cfg.payfastFeePct * 100).toFixed(1)}% on projected revenue ` +
        `(R${(baseRevTotal * cfg.payfastFeePct).toFixed(2)}/mo). ` +
        `Recurring values + PayFast % editable in Settings → System.`,
    };
  },

  async listExpenses(yearMonth?: string): Promise<ExpenseRow[]> {
    let query = supabase
      .from('expense')
      .select('id, amount, category, description, incurred_on, notes, recorded_by, created_at')
      .order('incurred_on', { ascending: false })
      .limit(200);
    if (yearMonth) {
      const ymParts = yearMonth.split('-').map(Number);
      const y = ymParts[0] ?? 1970;
      const m = ymParts[1] ?? 1;
      const start = new Date(Date.UTC(y, m - 1, 1)).toISOString().slice(0, 10);
      const end = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
      query = query.gte('incurred_on', start).lte('incurred_on', end);
    }
    const { data, error } = await query;
    if (error) throw new Error(`Could not load expenses: ${error.message}`);
    return (data ?? []) as ExpenseRow[];
  },

  async addExpense(input: {
    amount: number;
    category: ExpenseCategory;
    description: string;
    incurred_on: string;
    notes?: string;
  }): Promise<ExpenseRow> {
    const { data: user } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('expense')
      .insert({
        amount: input.amount,
        category: input.category,
        description: input.description,
        incurred_on: input.incurred_on,
        notes: input.notes ?? null,
        recorded_by: user?.user?.id ?? null,
      })
      .select('id, amount, category, description, incurred_on, notes, recorded_by, created_at')
      .single();
    if (error) throw new Error(`Could not record expense: ${error.message}`);
    return data as ExpenseRow;
  },

  async deleteExpense(id: string): Promise<void> {
    const { error } = await supabase.from('expense').delete().eq('id', id);
    if (error) {
      if (error.code === '42501') {
        throw new Error('Forbidden — only super_admin can delete expenses.');
      }
      throw new Error(`Could not delete: ${error.message}`);
    }
  },
};
